/**
 * ProjectRax — PWA Context
 * Manages: install prompt, update detection, online/offline status.
 * Provides hooks and components for PWA UX.
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const PWAContext = createContext(null);

export const usePWA = () => useContext(PWAContext);

export const PWAProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);
  const deferredPromptRef = useRef(null);
  const newWorkerRef = useRef(null);

  // ─── Online/Offline tracking ───
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // ─── Install Prompt capture ───
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      // Only show if not previously dismissed this session
      const dismissed = sessionStorage.getItem('prax-install-dismissed');
      if (!dismissed) {
        setIsInstallable(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Detect app installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      deferredPromptRef.current = null;
      console.log('[PWA] App installed');
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // ─── Update Detection ───
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleUpdate = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                newWorkerRef.current = newWorker;
                setUpdateAvailable(true);
              }
            });
          }
        });
      } catch (err) {
        console.warn('[PWA] Update detection failed:', err);
      }
    };

    handleUpdate();
  }, []);

  // ─── Actions ───
  const installApp = useCallback(async () => {
    if (!deferredPromptRef.current) return false;
    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    deferredPromptRef.current = null;
    setIsInstallable(false);
    return outcome === 'accepted';
  }, []);

  const dismissInstall = useCallback(() => {
    setIsInstallable(false);
    setInstallDismissed(true);
    sessionStorage.setItem('prax-install-dismissed', 'true');
  }, []);

  const applyUpdate = useCallback(() => {
    if (newWorkerRef.current) {
      newWorkerRef.current.postMessage('skipWaiting');
    }
    window.location.reload();
  }, []);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  return (
    <PWAContext.Provider value={{
      isOnline, isInstallable, isInstalled, updateAvailable, installDismissed,
      installApp, dismissInstall, applyUpdate, dismissUpdate,
    }}>
      {children}
    </PWAContext.Provider>
  );
};
